import { useQuery } from '@tanstack/react-query';

import { callService } from '@api/remote-call';
import {
  BasketViewModel,
  HotelGetAdvancePaymentRequestViewModel,
  LoyaltyType,
  BasketLoyaltyInfoViewModel,
  BasketSeturLoyaltyInfoViewModel,
} from '@api/v3';
import { isClientSide } from '@utils/env';

export type BasketType = BasketViewModel & {
  loyaltyInfo?: BasketLoyaltyInfoViewModel;
  seturLoyaltyProductsInfo?: BasketSeturLoyaltyInfoViewModel;
};

export const getBasketFromCache = (): BasketType | undefined => {
  if (typeof window !== 'undefined') {
    const basketDetail = localStorage.getItem('basketDetail');
    return basketDetail
      ? JSON.parse(basketDetail)
      : { tours: [], hotels: [], flights: [] };
  }
};

export const useBasketId = (): string | undefined => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('basketId') ?? undefined;
  }
};

export const useIsKocAilemValidated = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!getLoyalties()?.['activatedLoyalties']?.includes('KocAilem');
};
export const getLoyalties = () => {
  if (!isClientSide()) return {};
  return getBasketFromCache()?.loyaltyInfo || {};
};
export const isLoyaltyValidated = (loyaltyType: LoyaltyType) => {
  if (!isClientSide()) return false;
  return (
    !!getBasketFromCache()?.loyaltyInfo?.activatedLoyalties?.find(
      (activatedLoyalty) =>
        loyaltyType?.toLocaleLowerCase() ===
        activatedLoyalty?.toLocaleLowerCase()
    ) || !!getLoyalties()[loyaltyType?.toLocaleLowerCase()]
  );
};

export const storeLoyalty = <V>(loyalty: LoyaltyType, value?: V) => {
  if (isClientSide()) {
    const loyaltiesString = localStorage.getItem('loyalties');
    const loyalties = loyaltiesString ? JSON.parse(loyaltiesString) : {};
    loyalties[loyalty.toLowerCase()] = value;
    localStorage.setItem('loyalties', JSON.stringify(loyalties));
  }
};

export const clearLoyalty = (loyalty: LoyaltyType) => {
  if (isClientSide()) {
    const loyalties = getLoyalties();
    const key = loyalty.toLowerCase();
    if (key) {
      delete loyalties[key];
    }

    localStorage.setItem('loyalties', JSON.stringify(loyalties));
  }
};

export const useHotelsItemIds = (): string[] => {
  const basket = getBasketFromCache();
  return (
    basket?.hotels?.map((hotel) => hotel.itemId as NonNullable<string>) ?? []
  );
};
export const useFlightsItemIds = (): string[] => {
  const basket = getBasketFromCache();
  return (
    basket?.flights?.map((flight) => flight.itemId as NonNullable<string>) ?? []
  );
};

export const useIsDomesticFlight = (index = 0) => {
  const basket = getBasketFromCache();
  return basket?.flights[index]?.domestic ?? true;
};

export const useCanPayWithAdvance = (
  body?: HotelGetAdvancePaymentRequestViewModel,
  enabled?: boolean
) => {
  return useQuery(
    ['pay', body],
    async () =>
      callService(
        {
          service: 'PaymentsService',
          method: 'advancePaymentHotel',
          version: 'v3',
        },
        { body }
      ),
    {
      enabled,
      cacheTime: Infinity,
      staleTime: Infinity,
      onSuccess: (data) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem('advanceDeadline', `${data.deadLine}`);
        }
      },
    }
  );
};
